import { ALL_LOCATIONS, PROVINCE_BY_SLUG } from '@/app/lib/constants'

import { routes } from './routes'

/**
 * Sustituye el segmento de localización del `pathname` de la página actual
 * por `newLocation`, cuando ese segmento existe — permitiendo navegar sin
 * recarga completa a la variante localizada equivalente (FR-006/FR-007 de
 * specs/024-vistos-recientemente, research.md §5). Devuelve `null` cuando la
 * página actual no tiene ningún segmento de localización addressable (p. ej.
 * una ficha de evento/artista): en ese caso, el llamador solo debe persistir
 * la cookie, sin navegar.
 *
 * Todas las páginas localizables actuales (`home`/`home-localizado`,
 * `lo-mas-vendido`, `agenda`, `recintos`, `promociones`, PLP de categoría)
 * representan la localización como un único segmento de ruta bajo
 * `/entradas/...` (`src/app/lib/routing/routes.ts`) — nunca como estado
 * oculto — así que basta con localizar y sustituir ese segmento por su slug.
 *
 * Caso especial — home "bare" (`/entradas/`, `currentLocation ===
 * ALL_LOCATIONS`): no hay ningún segmento de provincia que sustituir, pero SÍ
 * hay una URL equivalente para `newLocation` (`routes.homeByLocation`, el
 * mismo segmento único que `src/proxy.ts` reescribe internamente al home
 * localizado). El camino inverso (`newLocation === ALL_LOCATIONS`) quita ese
 * único segmento y vuelve a `/entradas/`.
 *
 * Caso especial — categoría "bare" (`/entradas/{categoria}/`, DOS segmentos,
 * sin localización todavía — `[slug]/page.tsx`, p. ej. cuando `Agenda`/
 * `ProductosLocalizados` se embeben ahí vía CMS): tampoco hay segmento de
 * provincia que sustituir, pero sí una variante addressable simplemente
 * añadiéndolo — a diferencia de la home, no hace falta un builder de
 * `routes.*` dedicado: el propio segmento de categoría ya está en el
 * `pathname` recibido, y el resultado (`/entradas/{categoria}/{newLocation}/`)
 * es la misma forma de ruta que `[slug]/[slug2]/page.tsx` ya resuelve como
 * "categoría + localización" (`isLocation`, allow-list de provincias) — sin
 * ambigüedad porque `newLocation` es siempre un slug de provincia válido.
 *
 * Promovido aquí (antes colocado en `modules/ProductosLocalizados/utils/`)
 * cuando el módulo `Agenda` pasó a necesitar la misma lógica para su propio
 * selector de localización (specs/042-modulo-agenda-cms) — Principio II:
 * "local primero, global solo con reutilización real".
 */
export function buildLocalizedPath(pathname: string, currentLocation: string, newLocation: string): string | null {
  if (currentLocation === newLocation) return null

  const segments = pathname.split('/').filter(Boolean)
  const currentIndex = segments.findIndex((segment) => PROVINCE_BY_SLUG.has(segment) && segment === currentLocation)

  if (currentIndex !== -1) {
    const next = [...segments]
    if (newLocation === ALL_LOCATIONS) {
      next.splice(currentIndex, 1)
    } else {
      next[currentIndex] = newLocation
    }
    return `/${next.join('/')}/`
  }

  // Sin segmento de la localización actual y con `newLocation` concreta:
  // home y categoría "bare" tienen una variante addressable añadiéndolo.
  if (currentLocation === ALL_LOCATIONS && newLocation !== ALL_LOCATIONS) {
    const isBareHome = segments.length === 1 && segments[0] === 'entradas'
    if (isBareHome) return routes.homeByLocation(newLocation)

    const isBareCategory = segments.length === 2 && segments[0] === 'entradas'
    if (isBareCategory) return `/${segments.join('/')}/${newLocation}/`
  }

  return null
}
